import { singaporeTodayDateString } from '../utils/dateTime';
import { CLIENT_LOGO_BUCKET } from './clientLogo';
import { scopedStoragePath } from './portal';
import { queueSharePointEnquirySync } from './sharepointEnquiry';
import { getDbSupabase } from './supabase';

function isMissingColumnError(message: string): boolean {
  return /completed_date|completed_at|column|schema|PGRST204/i.test(message);
}

/** Try status patch; fall back when optional completion columns are missing from DB. */
async function updateProjectStatusPatch(
  projectId: string,
  patch: Record<string, unknown>,
): Promise<void> {
  const { error } = await getDbSupabase().from('projects').update(patch).eq('id', projectId);
  if (error) throw error;
}

async function applyProjectStatusUpdate(projectId: string, newStatus: string): Promise<void> {
  const isCompleted = newStatus === 'Completed';
  const today = singaporeTodayDateString();
  const nowIso = new Date().toISOString();

  const attempts: Record<string, unknown>[] = isCompleted
    ? [
        { status: newStatus, completed_date: today, completed_at: nowIso },
        { status: newStatus, completed_date: today },
        { status: newStatus, completed_at: nowIso },
        { status: newStatus },
      ]
    : [
        { status: newStatus, completed_date: null, completed_at: null },
        { status: newStatus, completed_date: null },
        { status: newStatus, completed_at: null },
        { status: newStatus },
      ];

  let lastError: Error | null = null;

  for (const patch of attempts) {
    try {
      await updateProjectStatusPatch(projectId, patch);
      return;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (!isMissingColumnError(message)) {
        throw err;
      }
      lastError = err instanceof Error ? err : new Error(message);
    }
  }

  if (lastError) throw lastError;
}

export async function changeProjectStatus(
  projectId: string,
  newStatus: string,
  logAuthorId: string,
): Promise<void> {
  await applyProjectStatusUpdate(projectId, newStatus);

  const { error: logError } = await getDbSupabase().from('project_updates').insert({
    project_id: projectId,
    content: `⚙️ Status changed to '${newStatus}'`,
    author_id: logAuthorId,
  });
  if (logError) throw logError;
  queueSharePointEnquirySync();
}

export async function postProjectUpdate(params: {
  projectId: string;
  authorId: string;
  message: string;
  file?: File | null;
}): Promise<void> {
  const { projectId, authorId, message, file } = params;
  let attachment_url: string | null = null;
  let attachment_name: string | null = null;

  if (file) {
    const ext = file.name.split('.').pop()?.toLowerCase() || 'bin';
    const remotePath = scopedStoragePath(`project_attachments/${projectId}_${Date.now()}.${ext}`);
    const { error: uploadError } = await getDbSupabase().storage
      .from(CLIENT_LOGO_BUCKET)
      .upload(remotePath, file, { cacheControl: '3600', upsert: false });
    if (uploadError) throw uploadError;
    attachment_url = remotePath;
    attachment_name = file.name;
  }

  const trimmed = message.trim();
  const content =
    trimmed || (attachment_name ? `Uploaded attachment: ${attachment_name}` : '');

  if (!content) {
    throw new Error('Please enter a message or attach a file.');
  }

  const { error } = await getDbSupabase().from('project_updates').insert({
    project_id: projectId,
    author_id: authorId,
    content,
    attachment_url,
    attachment_name,
  });
  if (error) throw error;
  queueSharePointEnquirySync();
}

export async function getProjectAttachmentUrl(path: string): Promise<string | null> {
  const { data, error } = await getDbSupabase().storage
    .from(CLIENT_LOGO_BUCKET)
    .createSignedUrl(path, 60 * 60);
  if (!error && data?.signedUrl) return data.signedUrl;
  const { data: pub } = getDbSupabase().storage.from(CLIENT_LOGO_BUCKET).getPublicUrl(path);
  return pub.publicUrl || null;
}
